/**
 * @description Component-TradeMobileProfile
 */
import './index.less';
import './MobileProfileItem.less';

import classNames from 'classnames';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { GlobalModalType } from '@/constants';
import { useGlobalConfig, useIsIpBlocked, useToggleModal } from '@/features/global/hooks';
import { useCurrentPairFromUrl } from '@/features/pair/hook';
import { cancelTradeOrders, setIsChosenOrder } from '@/features/trade/actions';
import { useBulkCancelStatus } from '@/features/trade/hooks';
import { useWalletConnectStatus } from '@/features/wallet/hook';
import { useAppDispatch } from '@/hooks';
import { useChainId, useUserAddr } from '@/hooks/web3/useChain';
import IconLastTrade from '@/pages/mobile/assets/svg/icon_last_trade_hover.svg?react';
import { WALLET_CONNECT_STATUS } from '@/types/wallet';

import { Button } from '@/components/Button';
import Dropdown from '@/components/Dropdown';
import SynTitleTabs from '@/components/Tabs/SynTitleTabs';
import { WrappedOrder } from '@/entities/WrappedOrder';
import { useWrappedPortfolioList } from '@/features/account/portfolioHook';
import { useTradeStore } from '@/features/trade/store';
import { useAppProvider, useSDK } from '@/features/web3/hook';
import { useFilteredOrders } from '@/hooks/trade/useFilteredOrders';
import { useTxNotification } from '@/hooks/useTxNotification';
import { useSwitchNetwork } from '@/hooks/web3/useSwitchConnectorNetwork';
import { useWalletSigner } from '@/hooks/web3/useWalletNetwork';
import { TabKeys } from '@/pages/trade/TradePortfolio/TradePortfolioTabs';
import { useLocation } from 'react-router-dom';
import PortfolioMobileAccounts from '../../PortfolioMobile/PortfolioMobileAccounts';
import TradeHistoryDrawer from '../../TradeMobile/TradeHistoryDrawer';
import TradeOrdersMobile from '../../TradeMobile/TradeOrdersMobile';
import TradePositionMobile from '../../TradeMobile/TradePositionMobile';
import CheckboxOff from './assets/icon_checkbox_off.svg?react';
import CheckboxOn from './assets/icon_checkbox_on.svg?react';
import IconMore from './assets/icon_more.svg?react';
import EarnLiquiditiesMobile from './EarnLiquiditiesMobile';

interface IPropTypes {
  children?: React.ReactNode;
  className?: string;
  isTradePage?: boolean;
}
const MobileProfile: FC<IPropTypes> = function ({ isTradePage = true }) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const chainId = useChainId();
  const userAddr = useUserAddr();

  const [activeTab, setActiveTab] = useState<TabKeys>(TabKeys.POSITIONS);

  const currentPair = useCurrentPairFromUrl(chainId);
  const [historyOpen, setHistoryOpen] = useState(false);

  const walletConnectStatus = useWalletConnectStatus();
  const toggleWalletModal = useToggleModal(GlobalModalType.Wallet);
  const isIpBlocked = useIsIpBlocked();
  const location = useLocation();
  const { hideOtherPair, setHideOtherPair } = useTradeStore();

  // For Cancel All
  const openOrders = useFilteredOrders(currentPair, true);
  const bulkCancelling = useBulkCancelStatus(chainId);

  const sdkContext = useSDK(chainId);
  const provider = useAppProvider();
  const { deadline } = useGlobalConfig(chainId);
  const signer = useWalletSigner();
  const notification = useTxNotification();
  const { switchWalletNetwork } = useSwitchNetwork();
  const portfolios = useWrappedPortfolioList(chainId, userAddr);

  const cancelOrders = useCallback(
    async (orders: WrappedOrder[], bulkCancelling?: boolean) => {
      if (chainId && userAddr && orders && sdkContext && signer && portfolios && deadline && provider) {
        const firstOrder = orders[0];
        const portfolio = portfolios.find((p) => p.rootPair.id === firstOrder?.rootPair.id);
        if (!portfolio) {
          return;
        }
        if (walletConnectStatus === WALLET_CONNECT_STATUS.WRONG_NETWORK) {
          await switchWalletNetwork(chainId);
          return;
        }
        dispatch(
          cancelTradeOrders({
            chainId,
            userAddr,
            sdkContext,
            signer,
            portfolio,
            orders: orders,
            deadline: Number(deadline),
            provider,
            bulkCancel: bulkCancelling,
            sendTxNotification: notification?.open,
          }),
        );
      }
    },
    [
      chainId,
      deadline,
      dispatch,
      portfolios,
      notification?.open,
      provider,
      sdkContext,
      signer,
      switchWalletNetwork,
      userAddr,
      walletConnectStatus,
    ],
  );

  const handleCancelAll = useCallback(() => {
    if (openOrders && openOrders.length > 0) {
      cancelOrders(openOrders);
    }
  }, [openOrders, cancelOrders]);

  const menuItems = useMemo(() => {
    const menus: {
      key: string;
      label: JSX.Element;
      disabled?: boolean;
    }[] = [
      {
        key: 'hideOtherPair',
        label: (
          <div
            className="syn-mobile-profile-menu-item"
            onClick={(e) => {
              e.stopPropagation();
              setHideOtherPair(!hideOtherPair);
            }}
            style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            {hideOtherPair ? <CheckboxOn /> : <CheckboxOff />}
            <span>{t('common.tradePortfolio.hideOtherPair', 'Hide Other Pair')}</span>
          </div>
        ),
      },
    ];
    if (activeTab === TabKeys.OPEN_ORDERS && isTradePage && hideOtherPair && openOrders?.length > 0) {
      menus.push({
        key: 'cancelAll',
        label: (
          <div
            className="syn-mobile-profile-menu-item"
            onClick={(e) => {
              e.stopPropagation();
              handleCancelAll();
            }}>
            {t('common.tradePage.cancelAll', 'Cancel All')}
          </div>
        ),
        disabled: !hideOtherPair || openOrders?.length === 0 || bulkCancelling,
      });
      // menus.push({
      //   key: 'claimAll',
      //   label: (
      //     <div
      //       className="syn-mobile-profile-menu-item"
      //       onClick={(e) => {
      //         e.stopPropagation();
      //         handleClaimAll();
      //       }}>
      //       {t('common.claimAll', 'Claim all')}
      //     </div>
      //   ),
      // });
    }

    return menus;
  }, [hideOtherPair, t, activeTab, isTradePage, setHideOtherPair, openOrders?.length, bulkCancelling, handleCancelAll]);
  // const openOrders = useWrappedOrderList(chainId, userAddr, currentPair?.id);
  // const rangeList = useWrappedRangeList(chainId, userAddr, currentPair?.id);

  // const dappConfig = useBackendChainConfig(chainId);
  const items = useMemo(() => {
    const res: { key: string; label: React.ReactNode }[] = [
      {
        key: TabKeys.POSITIONS,
        label: t('common.position'),
      },
      {
        key: TabKeys.OPEN_ORDERS,
        label: (
          <div
            className={classNames('syn-mobile-profile-tab-title', activeTab === TabKeys.OPEN_ORDERS ? 'selected' : '')}>
            <span>{t('common.tradePage.openO')}</span>
            {/* {!!openOrders?.length && <div className="syn-mobile-profile-tab-count">{openOrders?.length || 0}</div>} */}
          </div>
        ),
      },
      {
        key: TabKeys.LIQUIDITY,
        label: (
          <div
            className={classNames('syn-mobile-profile-tab-title', activeTab === TabKeys.LIQUIDITY ? 'selected' : '')}>
            <span>{t('common.earn.myLiq')}</span>
            {/* {!!rangeList?.length && <div className="syn-mobile-profile-tab-count">{rangeList?.length || 0}</div>} */}
          </div>
        ),
      },
    ];

    if (!isTradePage) {
      res.push({
        key: TabKeys.ACCOUNT,
        label: (
          <div className={classNames('syn-mobile-profile-tab-title', activeTab === TabKeys.ACCOUNT ? 'selected' : '')}>
            <span>{t('common.account')}</span>
          </div>
        ),
      });
    }

    return res.filter((t) => t !== undefined) as {
      key: string;
      label: React.ReactNode;
      children: React.ReactNode;
    }[];
  }, [t, activeTab, isTradePage]);

  const onChange = useCallback(
    (e: string) => {
      console.log('🚀 ~ file: index.tsx:53 ~ onChange ~ e:', e);
      // chainId && dispatch(setTradePortfolioTab({ tab: e as TabKeys, chainId }));
      setActiveTab(e as TabKeys);
    },
    [chainId, dispatch],
  );

  useEffect(() => {
    if (chainId) {
      dispatch(setIsChosenOrder({ isChosenOrder: false, chainId }));
    }
    // only depend on these
  }, [activeTab, dispatch, chainId]);

  useEffect(() => {
    const { tab } = location.state || {};
    if (tab) {
      onChange(tab as TabKeys);
    }
  }, [location.state, onChange]);

  return (
    <div className={classNames('syn-mobile-profile')}>
      <div className="syn-mobile-profile-header">
        <SynTitleTabs
          wrapStyle={{ height: 44 }}
          className="syn-mobile-profile-tabs"
          activeKey={activeTab}
          items={items}
          onClick={onChange}
        />
        <div className="syn-mobile-profile-header-btns">
          <Button
            disabled={isIpBlocked}
            onClick={() => {
              if (walletConnectStatus === WALLET_CONNECT_STATUS.UN_CONNECT) {
                toggleWalletModal();
                return;
              }
              setHistoryOpen(true);
            }}
            type="text">
            <IconLastTrade />
          </Button>
          {isTradePage && (
            <Dropdown
              menu={{ items: menuItems }}
              placement="bottomRight"
              trigger={['click']}
              overlayClassName="syn-mobile-profile-more-dropdown">
              <Button disabled={isIpBlocked} type="text" className="syn-mobile-profile-more-btn">
                <IconMore />
              </Button>
            </Dropdown>
          )}
        </div>
      </div>
      {activeTab === TabKeys.POSITIONS && <TradePositionMobile />}
      {activeTab === TabKeys.OPEN_ORDERS && <TradeOrdersMobile />}
      {activeTab === TabKeys.LIQUIDITY && <EarnLiquiditiesMobile />}
      {activeTab === TabKeys.ACCOUNT && <PortfolioMobileAccounts />}

      <TradeHistoryDrawer open={historyOpen} onClose={() => setHistoryOpen(false)} isTradePage={isTradePage} />
    </div>
  );
};

export default MobileProfile;
